import React, {useEffect, useState} from "react";
import {Modal, Form, Input, Select, message, InputNumber, Switch, Row, Col} from "antd";
import {useTranslation} from "react-i18next";
import {useStore} from "../../../hooks/useStore";
import {UserFilter} from "../../_defaultComponents/FilterComponents";
const {Option} = Select;

export const AdjustmentTypeModal = ({visible, onCancel, onSuccess, editData = null}) => {
    const {t} = useTranslation(["common"]);
    const stores = useStore();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [category, setCategory] = useState(null);

    const isEditMode = !!editData;

    useEffect(() => {
        if (visible && editData) {
            // Edit modunda formu doldur
            form.setFieldsValue({
                name: editData.name,
                type: editData.type,
                authorizedUsers: editData.authorizedUsers?.map(u => u._id) || [],
                stockCode: editData.stockCode || null,
                category: editData.category || null,
                exemptionCode: editData.exemptionCode || null,
                kdvRate: editData.kdvRate ?? null,
                // Form alanları minValue / maxValue; API/liste genelde minAmount / maxAmount gönderir
                minValue: editData.minAmount ?? editData.minValue ?? null,
                maxValue: editData.maxAmount ?? editData.maxValue ?? null,
                isEquipment: !!editData.isEquipment,
                canApplyInstantly: !!editData.canApplyInstantly
            });
            setCategory(editData.category || null);
        } else if (visible && !editData) {
            setCategory(null);
        } else if (!visible) {
            // Modal kapandığında formu temizle
            form.resetFields();
            setCategory(null);
        }
    }, [visible, editData, form]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                name: values.name,
                type: values.type,
                authorizedUserIds: values.authorizedUsers || [],
                stockCode: values.stockCode || null,
                category: values.category || null,
                exemptionCode: values.exemptionCode || null,
                minAmount: values.minValue ?? null,
                maxAmount: values.maxValue ?? null,
                isEquipment: Boolean(values.isEquipment),
                canApplyInstantly: Boolean(values.canApplyInstantly),
                ...(values.category === "financial" &&
                values.kdvRate !== undefined &&
                values.kdvRate !== null
                    ? {kdvRate: values.kdvRate}
                    : {})
            };

            let result;
            if (isEditMode) {
                result = await stores.courierAdjustmentStore.updateAdjustmentType(
                    editData._id,
                    payload
                );
            } else {
                result = await stores.courierAdjustmentStore.createAdjustmentType(payload);
            }

            if (result && result.success !== false) {
                form.resetFields();
                onSuccess();
            }
        } catch (error) {
            console.error("Form validation error:", error);
            if (error.errorFields) {
                message.error("Lütfen tüm zorunlu alanları doldurun.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title={isEditMode ? "Eklenti/Kesinti Tipi Düzenle" : "Yeni Eklenti/Kesinti Tipi"}
            visible={visible}
            onOk={handleSubmit}
            onCancel={onCancel}
            confirmLoading={loading}
            okText={t("actions.save", {ns: "common"})}
            cancelText="İptal"
            width={600}
        >
            <Form form={form} layout="vertical">
                <Form.Item
                    label="İsim"
                    name="name"
                    rules={[
                        {required: true, message: "İsim zorunludur"},
                        {max: 100, message: "İsim en fazla 100 karakter olabilir"}
                    ]}
                >
                    <Input placeholder="Örn: ÖSS, İkramiye, Prim Kesinti" />
                </Form.Item>

                <Form.Item
                    label="Tip"
                    name="type"
                    rules={[{required: true, message: "Tip seçimi zorunludur"}]}
                >
                    <Select placeholder="Tip seçin">
                        <Option value="addition">Eklenti</Option>
                        <Option value="deduction">Kesinti</Option>
                    </Select>
                </Form.Item>

                <Form.Item
                    label="Yetkili Kullanıcılar"
                    name="authorizedUsers"
                    tooltip="Bu tipi kullanabilecek kullanıcıları seçin"
                >
                    <UserFilter
                        hideTitle
                        value={form.getFieldValue("authorizedUsers")}
                        onChange={value => form.setFieldsValue({authorizedUsers: value})}
                        placeholder="Kullanıcıları seçin"
                        selectMarginBottom="0px"
                        excludedRoles={[
                            "bm",
                            "agency",
                            "sy",
                            "hr",
                            "hrEmployment",
                            "ik",
                            "agencyIk",
                            "cUser",
                            "oUser",
                            "operationSupport",
                            "software",
                            "marketing"
                        ]}
                        additionalOptions={
                            editData?.authorizedUsers?.map(u => ({
                                _id: u._id?.toString() || "",
                                fullName: u.name || ""
                            })) || []
                        }
                    />
                </Form.Item>
                <Form.Item label="Netsis Stok Kodu" name="stockCode" rules={[{required: false}]}>
                    <Input placeholder="Netsis Stok Kodu" />
                </Form.Item>
                <Form.Item
                    label="Kategori"
                    name="category"
                    rules={[{required: true, message: "Kategori seçimi zorunludur"}]}
                >
                    <Select
                        placeholder="Kategori seçin"
                        allowClear
                        onChange={value => {
                            const next = value ?? null;
                            setCategory(next);
                            if (next !== "financial") {
                                form.setFieldsValue({kdvRate: null});
                            }
                        }}
                    >
                        <Option value="financial">Finansal</Option>
                        <Option value="financialWithoutInvoice">Faturasız Finansal</Option>
                        <Option value="other">Diğer</Option>
                    </Select>
                </Form.Item>
                <Form.Item label="İstisna Kodu" name="exemptionCode" rules={[{required: false}]}>
                    <Input placeholder="İstisna Kodu" />
                </Form.Item>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label="KDV Oranı"
                            name="kdvRate"
                            dependencies={["category"]}
                            rules={[
                                ({getFieldValue}) => ({
                                    validator(_, value) {
                                        if (getFieldValue("category") === "financial") {
                                            if (
                                                value === undefined ||
                                                value === null ||
                                                value === ""
                                            ) {
                                                return Promise.reject(
                                                    new Error("KDV Oranı zorunludur")
                                                );
                                            }
                                        }
                                        return Promise.resolve();
                                    }
                                })
                            ]}
                        >
                            <InputNumber
                                placeholder="KDV Oranı (%)"
                                min={0}
                                max={100}
                                disabled={category !== "financial"}
                                style={{width: "100%"}}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={24} md={12}>
                        <Form.Item label="Girilebilecek Min ve Maks Değer" required>
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                    width: "100%"
                                }}
                            >
                                <Form.Item
                                    name="minValue"
                                    noStyle
                                    dependencies={["maxValue"]}
                                    rules={[
                                        {required: true, message: "Minimum değer zorunludur"},
                                        ({getFieldValue}) => ({
                                            validator(_, value) {
                                                const max = getFieldValue("maxValue");
                                                if (
                                                    value != null &&
                                                    max != null &&
                                                    Number(value) > Number(max)
                                                ) {
                                                    return Promise.reject(
                                                        new Error("Min, maksimumdan büyük olamaz")
                                                    );
                                                }
                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                >
                                    <InputNumber placeholder="Min" style={{width: "100%"}} />
                                </Form.Item>
                                <span
                                    aria-hidden
                                    style={{
                                        flexShrink: 0,
                                        color: "rgba(0, 0, 0, 0.45)",
                                        userSelect: "none"
                                    }}
                                >
                                    —
                                </span>
                                <Form.Item
                                    name="maxValue"
                                    noStyle
                                    dependencies={["minValue"]}
                                    rules={[
                                        {required: true, message: "Maksimum değer zorunludur"},
                                        ({getFieldValue}) => ({
                                            validator(_, value) {
                                                const min = getFieldValue("minValue");
                                                if (
                                                    value != null &&
                                                    min != null &&
                                                    Number(value) < Number(min)
                                                ) {
                                                    return Promise.reject(
                                                        new Error("Maks, minimumdan küçük olamaz")
                                                    );
                                                }
                                                return Promise.resolve();
                                            }
                                        })
                                    ]}
                                >
                                    <InputNumber placeholder="Maks" style={{width: "100%"}} />
                                </Form.Item>
                            </div>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 16]} align="bottom">
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label="Ekipman Mı?"
                            name="isEquipment"
                            valuePropName="checked"
                            rules={[{required: false}]}
                            style={{marginBottom: 0}}
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                    <Col xs={24} sm={12} md={12}>
                        <Form.Item
                            label="Cüzdana anlık uygulanabilir mi?"
                            name="canApplyInstantly"
                            valuePropName="checked"
                            rules={[{required: false}]}
                            style={{marginBottom: 0}}
                        >
                            <Switch />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};
